import React from "react";
import dynamic from "next/dynamic";
import { ChevronLeft } from "lucide-react";

import {
  Dialog,
  DialogTitle,
  DialogHeader,
  DialogContent,
} from "@/components/ui/dialog";
import { Paragraph, Text } from "@/components/ui/typography";
import { Button } from "@/components/ui/button";

const ShakaPlayer = dynamic(() => import("@/components/shaka-player"));

import { cn } from "@/lib/utils";

function DemoVideosModal({ videoId, setVideoId, videos, courseId }) {
  const [video, setVideo] = React.useState(null);

  React.useEffect(() => {
    const currentVideo = videos.find((v) => v.id === videoId);
    if (!currentVideo) return;

    setVideo(currentVideo);
  }, [videoId, videos]);

  if (!videos?.length || !video) return null;

  const currentVideoIndex = videos.findIndex((v) => v.id === video.id);

  const handlePrevClick = () => {
    if (currentVideoIndex <= 0) return;

    setVideo(videos[currentVideoIndex - 1]);
  };

  const handleNextClick = () => {
    if (currentVideoIndex >= videos.length - 1) return;

    setVideo(videos[currentVideoIndex + 1]);
  };

  return (
    <Dialog
      className="p-4"
      open
      onOpenChange={() => {
        setVideo(null);
        setVideoId(null);
      }}
    >
      <DialogContent className="w-full h-auto sm:max-w-5xl">
        <DialogHeader>
          <Paragraph
            variant="b5"
            weight="sb"
            className="text-grayscale-placeholder"
          >
            <span className="text-grayscale-body">
              {String(currentVideoIndex + 1).padStart(2, "0")}
            </span>
            /
            <Text variant="misc-regular">
              {String(videos.length).padStart(2, "0")}
            </Text>{" "}
            Demo Videos
          </Paragraph>
          <DialogTitle>{video.title}</DialogTitle>
        </DialogHeader>

        <ShakaPlayer
          video={{
            ...video,
            videoUrl: video.s3Enabled ? video.s3Url : video.videoUrl,
          }}
          courseId={courseId}
          className="relative aspect-video rounded-2xl overflow-hidden"
        />

        {videos.length > 1 && (
          <div className="absolute top-1/2 w-full flex justify-between gap-x-4 -translate-y-1/2">
            <Button
              variant="outline"
              size="icon"
              className={cn(
                "bg-primary-bg border-primary-bg-strong rounded-full transition-colors duration-300 -translate-x-full",
                "disabled:bg-grayscale-weak disabled:border-primary-weak disabled:[&>svg]:text-primary-weak"
              )}
              onClick={handlePrevClick}
              disabled={currentVideoIndex <= 0}
            >
              <ChevronLeft className="size-5" />
            </Button>

            <Button
              variant="outline"
              size="icon"
              className={cn(
                "bg-primary-bg border-primary-bg-strong rounded-full transition-colors duration-300 translate-x-full",
                "disabled:bg-grayscale-weak disabled:border-primary-weak disabled:[&>svg]:text-primary-weak"
              )}
              onClick={handleNextClick}
              disabled={currentVideoIndex >= videos.length - 1}
            >
              <ChevronLeft className="size-5 rotate-180" />
            </Button>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}

export default DemoVideosModal;
